import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import '../styles/fonts.css';

const Skill = () => {
    const navigate = useNavigate();
    const [hoveredSkill, setHoveredSkill] = useState(null);

    const skills = [
        {
            id: 'strategy',
            label: 'Strategy',
            angle: 0,
            color: '#000000',
            details: ['Architecture', 'Roadmapping', 'Risk Assessment'],
            description: "Defining the blueprint for scalable success."
        },
        {
            id: 'frontend',
            label: 'Frontend',
            angle: 60,
            color: '#000000',
            details: ['React', 'Vue', 'TypeScript', 'Animation'],
            description: "Crafting pixel-perfect, interactive interfaces."
        },
        {
            id: 'backend',
            label: 'Backend',
            angle: 120,
            color: '#000000',
            details: ['Node.js', 'Python', 'Databases', 'APIs'],
            description: "Building robust engines that power the experience."
        },
        {
            id: 'uiux',
            label: 'UI/UX',
            angle: 180,
            color: '#000000',
            details: ['Research', 'Wireframing', 'Prototyping'],
            description: "Designing with empathy for the human journey."
        },
        {
            id: 'automation',
            label: 'Automation',
            angle: 240,
            color: '#000000',
            details: ['CI/CD', 'Testing', 'Pipelines'],
            description: "Streamlining workflows for efficiency and reliability."
        },
        {
            id: 'healthcare',
            label: 'Healthcare',
            angle: 300,
            color: '#000000',
            details: ['HIPAA', 'Medical Data', 'OSH'],
            description: "Specialized knowledge in safety and compliance."
        },
    ];

    // Hexagon Math
    const radius = 180;
    const itemRadius = 260; // Distance for text labels
    const center = { x: 300, y: 300 };

    const getPoint = (r, angle) => {
        const radian = (angle - 90) * (Math.PI / 180);
        return {
            x: center.x + r * Math.cos(radian),
            y: center.y + r * Math.sin(radian)
        };
    };

    const points = skills.map(skill => getPoint(radius, skill.angle)).map(p => `${p.x},${p.y}`).join(' ');

    const pageVariants = {
        initial: { y: "-100vh", opacity: 0 },
        animate: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
        },
        exit: {
            y: "-100vh",
            opacity: 0,
            transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
        }
    };

    return (
        <motion.div
            className="min-h-screen bg-vintage-beige font-sans selection:bg-black selection:text-white overflow-hidden relative"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
        >
            {/* Navigation */}
            <motion.button
                onClick={() => navigate('/')}
                className="fixed top-0 left-0 z-50 flex flex-col items-center group hidden md:flex"
                style={{ padding: '20px' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
            >
                <motion.span
                    className="text-black text-sm font-medium"
                    style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)'
                    }}
                    whileHover={{ letterSpacing: "0.2em" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                >
                    HOME
                </motion.span>
                <motion.div
                    className="w-[1px] bg-black"
                    initial={{ height: '80px', opacity: 0.6 }}
                    whileHover={{ height: '100px', opacity: 1 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{
                        marginTop: '20px'
                    }}
                />
            </motion.button>

            {/* 2025 Vertical Text */}
            <div className="fixed left-8 bottom-8 z-50 hidden md:block">
                <span
                    className="text-black/40 text-[10px] font-sans tracking-widest block"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                    © 2025
                </span>
            </div>

            {/* Main Layout */}
            <div className="flex flex-col md:flex-row h-screen overflow-y-auto no-scrollbar pt-4 md:pt-0">
                {/* Mobile Top Bar - HOME + Line */}
                <div className="md:hidden flex items-center mb-6 w-full gap-4 px-8 mt-4">
                    <button
                        onClick={() => navigate('/')}
                        className="text-black text-sm font-medium tracking-widest uppercase"
                    >
                        HOME
                    </button>
                    <div className="h-[1px] bg-black w-24 opacity-20"></div>
                </div>

                {/* Left Content */}
                <div className="w-full md:w-1/3 p-8 md:p-20 flex flex-col pt-10 md:pt-40 relative z-10 shrink-0">
                    <motion.h1
                        className="text-5xl sm:text-7xl md:text-9xl bonky-font text-black mb-6 md:mb-8 leading-none"
                        initial={{ x: -50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        Capa-<br />bilities.
                    </motion.h1>

                    {/* Dynamic Detail Panel */}
                    <div className="min-h-[160px] md:h-48 mt-4">
                        <AnimatePresence mode="wait">
                            {hoveredSkill ? (
                                <motion.div
                                    key={hoveredSkill.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="space-y-4"
                                >
                                    <h3 className="text-xl md:text-2xl bonky-font text-black">{hoveredSkill.label}</h3>
                                    <p className="text-sm md:text-base text-gray-600 leading-relaxed font-light">{hoveredSkill.description}</p>
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        {hoveredSkill.details.map(tag => (
                                            <span key={tag} className="px-3 py-1 bg-black/5 rounded-full text-[10px] md:text-xs text-black/70 font-medium">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="default"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="flex items-center h-full"
                                >
                                    <p className="text-gray-400 italic text-sm md:text-lg font-light">
                                        Explore the graphic to see details across disciplines.
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Right Graphic Area */}
                <div className="w-full md:w-2/3 h-[50vh] md:h-full flex items-center justify-center relative bg-gradient-to-l from-black/5 to-transparent p-4 md:p-0">

                    {/* SVG Chart Container - Responsive scaling */}
                    <div className="relative w-full max-w-[320px] sm:max-w-[450px] md:max-w-[600px] aspect-square">
                        <svg viewBox="0 0 600 600" className="w-full h-full overflow-visible">
                            {/* Background Circles */}
                            {[0.3, 0.6, 1].map((scale, i) => (
                                <circle
                                    key={i}
                                    cx={center.x}
                                    cy={center.y}
                                    r={radius * scale}
                                    fill="none"
                                    stroke="#000"
                                    strokeOpacity="0.05"
                                    strokeWidth="1"
                                />
                            ))}

                            {/* Connecting Lines */}
                            {skills.map((skill, i) => {
                                const p = getPoint(radius, skill.angle);
                                return (
                                    <line
                                        key={i}
                                        x1={center.x}
                                        y1={center.y}
                                        x2={p.x}
                                        y2={p.y}
                                        stroke="#000"
                                        strokeOpacity="0.05"
                                        strokeWidth="1"
                                    />
                                );
                            })}

                            {/* Main Shape */}
                            <motion.polygon
                                points={points}
                                fill="rgba(0, 0, 0, 0.03)"
                                stroke="black"
                                strokeWidth="2"
                                initial={{ scale: 0, opacity: 0, rotate: -30 }}
                                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                                transition={{ duration: 1, ease: "easeOut" }}
                                className="drop-shadow-2xl"
                            />

                            {/* Vertices */}
                            {skills.map((skill) => {
                                const p = getPoint(radius, skill.angle);
                                const labelP = getPoint(itemRadius, skill.angle);

                                return (
                                    <g key={skill.id}
                                        onMouseEnter={() => setHoveredSkill(skill)}
                                        onMouseLeave={() => setHoveredSkill(null)}
                                        className="cursor-pointer group"
                                    >
                                        {/* Interactive Dot */}
                                        <motion.circle
                                            cx={p.x}
                                            cy={p.y}
                                            r={6}
                                            fill="black"
                                            initial={{ scale: 0 }}
                                            animate={{
                                                scale: hoveredSkill?.id === skill.id ? 1.5 : 1,
                                                fill: hoveredSkill?.id === skill.id ? "#000" : "#333"
                                            }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        />

                                        {/* Hover Ring */}
                                        {hoveredSkill?.id === skill.id && (
                                            <motion.circle
                                                cx={p.x}
                                                cy={p.y}
                                                r={12}
                                                stroke="black"
                                                strokeWidth="1"
                                                fill="none"
                                                initial={{ scale: 0.5, opacity: 0 }}
                                                animate={{ scale: 1.5, opacity: 0.5 }}
                                                exit={{ scale: 0.5, opacity: 0 }}
                                                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                                            />
                                        )}

                                        {/* Label */}
                                        <text
                                            x={labelP.x}
                                            y={labelP.y}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            className={`text-sm md:text-base font-sans tracking-wider transition-all duration-300 ${hoveredSkill?.id === skill.id ? 'fill-black font-bold' : 'fill-gray-400'}`}
                                        >
                                            {skill.label.toUpperCase()}
                                        </text>
                                    </g>
                                );
                            })}
                        </svg>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default Skill;
